<script>
import {computed, defineComponent, reactive, ref, watch} from "vue";
import {useStore} from "vuex";
import {useError} from "@/hooks/useErrors";
import {useRoles} from "@/hooks/role";
import {useNotices} from "@/hooks/notices";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputMask from "primevue/inputmask";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";
import Checkbox from "primevue/checkbox";
import Editor from "primevue/editor";
import MultiSelect from "primevue/multiselect";
import ButtonSuccess from "@/components/buttons/ButtonSuccess";
import Breadcrumb from "@/components/Breadcrumb.vue";
import MainCard from "@/components/cards/MainCard.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import {useApiCall} from "@/composables/useApiCall";
import {loadUsers} from "@/services/users";
import {loadAppRoles} from "@/services/roles";

export default defineComponent({
  layout: {name: 'AdminLayout'},
  components: {
    Breadcrumb, MainCard, InputText,
    InputMask, Dropdown, Calendar,
    Checkbox, MultiSelect, Button,
    Editor, ConfirmationModal, ButtonSuccess,
  },
  async beforeMount() {
    try {
      const {loadRoles, loadAppRoles} = useRoles();
      const {loadTypeNotices} = useNotices();
      await Promise.all([loadTypeNotices(), loadRoles(), loadAppRoles()]);
    } catch (e) {
      console.error(e);
    }
  },
  setup() {
    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const titleField = computed(() => `title${lang.value === 'ru' ? '' : `_${lang.value}`}`);
    const textField = computed(() => `text${lang.value === 'ru' ? '' : `_${lang.value}`}`);
    const errors = useError();
    const {roles, rolesApp} = useRoles();
    const {
      isCreate,
      typeNotices,
      formCreateNotice: form,
      createNotice: create,
      createNoticeAsDraft,
      v$
    } = useNotices();

    const allRoles = computed(() => {
      return [
          {id: -2, name: 'all', name_ru: 'Все пользователи'},
          ...(rolesApp.value.length ? rolesApp.value : []),
          ...(roles.value.length ? roles.value : []),
          {id: -1, name: 'user', name_ru: 'Определенный пользователь'},
      ]
    })

    const isOwnerId = computed(() => {
      const owner = allRoles.value.find(item => item.name === 'owner')
      return owner ? owner.id : null
    })

    const breadcrumbs = ref([
      {label: 'Уведомления', router: {name: 'notices'}},
      {label: 'Создание уведомления'}
    ]);

    const statuses = ref([
      {title: 'Все', value: 'all'},
      {title: 'В управлении', value: 'controlled'},
      {title: 'Не в управлении', value: 'uncontrolled'},
    ])

    const isSaveDraft = ref(false);
    const users = ref([]);
    const loading = ref(false);
    const {
      isLoading: isUsersLoading,
      data: usersData,
      error: usersError,
      executeApiCall: getUsersAction,
    } = useApiCall(loadUsers, true)

    const filter = ref('')

    watch(() => filter.value, async () => {
      await filterUser(filter.value)
    })

    const filterUser = async (value) => {
      loading.value = true;
      if (value) {
        await getUsersAction({page: 1, search: value})
        if (usersData.value) {
          const formatArray = []
          usersData.value.data.data.forEach(user => {
            formatArray.push({
              label: `${user.last_name ?? '-'} ${user.first_name ?? '-'} ${user.patronymic ?? '-'}`,
              value: user.id,
              phone: `${user.phone_code ? user.phone_code : ''}${user.phone}`,
            })
          })
          users.value = [...formatArray]
        }
      } else {
        users.value = []
      }
      loading.value = false;
    }

    const noticeTypeForm = reactive({
      name: ''
    });

    const visibleConfirmationModal = computed(() => store.getters.getCreateTypeNoticeModal);
    const changeVisible = () => store.dispatch('changeStateModal', {
      type: 'createTypeNoticeModal',
      bool: !visibleConfirmationModal.value
    });

    const createTypeNotice = async () => {
      await store.dispatch('fetchCreateTypeNotice', noticeTypeForm);
      await store.dispatch('fetchTypeNotices');
      await changeVisible();
    };

    const createNotice = async () => {
      errors.clearErrors();

      try {
        await create();
      } catch (e) {
        errors.setErrors(e.response.data.data);
        console.error(e);
      }
    }

    const saveAsDraft = async () => {
      errors.clearErrors();
      try {
        const isSuccess = await createNoticeAsDraft();
        isSaveDraft.value = isSuccess;
      } catch (e) {
        const responseErr = e.response.data.data;
        if (responseErr.text) {
          responseErr.text = ['Текст уведомления на русском языке обязателен'];
        }
        errors.setErrors(responseErr);
      }
    };

    const changeRole = (item) => {
      if (item.value !== -1) {
        form.value.user_id = null
        filter.value = ''
      }
    }

    return {
      v$,
      breadcrumbs,
      roles,
      typeNotices,
      form,
      noticeTypeForm,
      visibleConfirmationModal,
      changeVisible,
      createTypeNotice,
      createNotice,
      saveAsDraft,
      isSaveDraft,
      isCreate,
      errors: errors.errors,
      textField,
      statuses,
      isOwnerId,
      allRoles,
      changeRole,
      loading,
      users,
      filterUser,
      filter,
      titleField,
    };
  },
});
</script>
<style lang="css" scoped>
.calendar-icon {
  position: absolute;
  right: 15px;
  top: 25px;
  pointer-events: none;
}
.current-option {
  display: flex;
  flex-direction: column;
}
.current-option__phone {
  font-weight: 400;
  font-size: 11px;
  line-height: 18px;
  color: #666666;
}
.current-option__label {
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  vertical-align: middle;
  color: #333333;
}
.notice__wrapper {
  overflow: hidden;
}
.nowrap_label {
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100% - 40px) !important;
  white-space: nowrap !important;
  padding-right: 24px !important;
}
</style>

<template>
  <ConfirmationModal :visible="visibleConfirmationModal">
    <template #header>
      Создание типа уведомления
    </template>

    <template #default>
      <span class="p-float-label w-full">
        <InputText v-model="noticeTypeForm.name" id="name" class="w-full"/>
        <label for="name">Название типа</label>
      </span>
    </template>

    <template #footer>
      <div class="flex">
        <Button @click="changeVisible" label="Отмена" class="btn-error-outlined font-light"/>
        <Button @click="createTypeNotice" label="Создать тип" class="btn-primary font-light ml-3"/>
      </div>
    </template>
  </ConfirmationModal>

  <section class="py-2 mb-3">
    <div class="flex justify-content-between">
      <Breadcrumb :data="breadcrumbs" separator="/"/>

      <div class="flex">
        <template v-if="!isCreate">
          <ButtonSuccess v-if="isSaveDraft" label="Черновик сохранен"/>
          <Button @click="saveAsDraft" label="Сохранить как черновик" class="btn-black-20-outlined font-light ml-2"/>
          <Button @click="createNotice" label="Запланировать отправку" class="btn-primary font-light ml-2"/>
        </template>
        <template v-if="isCreate">
          <ButtonSuccess label="Уведомление создано"/>
        </template>
      </div>
    </div>
  </section>

  <section class="py-2 mb-3">
    <div class="grid">
      <div class="col-12">
        <MainCard title="Наименование уведомления">
          <span class="p-float-label w-full">
            <InputText
                v-model="form[titleField]"
                id="name"
                class="w-full"
                :class="{'p-invalid': v$[titleField].$errors.length || errors?.[titleField]}"
            />
            <label for="name">Наименование</label>
          </span>
          <span v-if="v$[titleField].$errors.length || errors?.[titleField]" class="text-xs color-error">
            {{ errors?.[titleField] ? errors[titleField][0] : v$[titleField].$errors[0].$message }}
          </span>
        </MainCard>
      </div>
      <div class="col-12 xl:col-4 md:col-6">
        <MainCard title="Выберите или создайте тип уведомления">
          <div class="w-full mb-2">
            <Dropdown
                v-model="form.push_type_id"
                :class="{'p-invalid': v$.push_type_id.$errors.length || errors?.push_type_id}"
                :options="typeNotices"
                optionLabel="name"
                option-value="id"
                placeholder="Тип уведомления"
                class="w-full"/>
            <span v-if="v$.push_type_id.$errors.length || errors?.push_type_id" class="text-xs color-error">
              {{ errors?.push_type_id ? errors.push_type_id[0] : v$.push_type_id.$errors[0].$message }}
            </span>
          </div>

          <span @click="changeVisible" class="color-primary underline cursor-pointer">
            Создать новый тип уведомления
          </span>
        </MainCard>
      </div>

      <div class="col-12 xl:col-4 md:col-6">
        <MainCard title="Дата и время отправки">
          <div class="grid">
            <div class="col-12 md:col-6 relative">
              <Calendar
                  v-model="form.send_date"
                  :class="{'p-invalid': errors.send_date}"
                  date-format="d.m.yy"
                  placeholder="Дата"
                  class="w-full"
              />
              <img src="@/assets/images/icons/calendar.svg" class="calendar-icon" alt="" />
              <span v-if="errors.send_date" class="text-xs color-error">
                {{ errors.send_date[0] }}
              </span>
            </div>
            <div class="col-12 md:col-6">
              <div class="w-full">
                <span class="p-input-icon-left p-float-label w-full">
                  <i class="pi pi-search"/>
                  <InputMask id="time" v-model="form.send_time" class="w-full" mask="99:99" placeholder="12:00"/>
                  <label for="time">Время</label>
                </span>
                <span v-if="errors.send_time" class="text-xs color-error">
                  {{ errors.send_time[0] }}
                </span>
              </div>
            </div>
          </div>

          <Checkbox v-model="form.send_now" :binary="true" name="send_now" id="send_now"/>
          <label for="send_now" class="ml-1">Отправить немедленно</label>
        </MainCard>
      </div>

      <div class="col-12 xl:col-4 md:col-6">
        <MainCard title="Срок действия данных">
          <div class="grid">
            <div class="col-12 relative">
              <Calendar
                  v-model="form.expiration_date"
                  :class="{'p-invalid': errors.expiration_date}"
                  date-format="d.m.yy"
                  placeholder="Дата окончания"
                  class="w-full"
              />
              <img src="@/assets/images/icons/calendar.svg" class="calendar-icon" alt="" />
              <span v-if="errors.expiration_date" class="text-xs color-error">
                {{ errors.expiration_date[0] }}
              </span>
            </div>
          </div>
        </MainCard>
      </div>
    </div>
  </section>

  <section class="py-2 mb-3">
    <div class="grid">
      <div class="col-12 xl:col-4 md:col-6">
        <MainCard title="Получатели уведомления">
          <div class="w-full mb-2">
            <Dropdown
                v-model="form.role"
                :class="{'p-invalid': v$.role.$errors.length || errors?.role}"
                :options="allRoles"
                optionLabel="name_ru"
                option-value="id"
                placeholder="Выберите получателей"
                class="w-full"
                @change="changeRole"
            />
            <span v-if="v$.role.$errors.length || errors?.role" class="text-xs color-error">
              {{ errors?.role ? errors.push_type_id[0] : v$.role.$errors[0].$message }}
            </span>
          </div>
        </MainCard>
      </div>

      <div class="col-12 xl:col-4 md:col-6">
        <MainCard v-if="isOwnerId && form.role === isOwnerId" title="Статус собственника">
          <div class="w-full mb-2">
            <Dropdown
                v-model="form.control_status"
                :class="{'p-invalid': v$.control_status.$errors.length || errors?.control_status}"
                :options="statuses"
                optionLabel="title"
                option-value="value"
                placeholder="Статус собственника"
                class="w-full"/>
            <span v-if="v$.control_status.$errors.length || errors?.control_status" class="text-xs color-error">
              {{ errors?.control_status ? errors.control_status[0] : v$.control_status.$errors[0].$message }}
            </span>
          </div>
        </MainCard>
        <MainCard v-if="form.role === -1" title="Найти пользователя">
          <div class="w-full p-float-label mb-2 notice__wrapper">
            <Dropdown
                id="users"
                v-model="form.user_id"
                :class="{'p-invalid': v$.user_id.$errors.length || errors?.user_id}"
                :options="users"
                optionLabel="label"
                placeholder="Введите ФИО или номер телефона"
                class="w-full"
                @filter="filterUser"
            >
              <template #value="slotProps">
                <div v-if="slotProps.value" class="current-option">
                  <span class="current-option__label">{{slotProps.value.label}}</span>
                  <span class="current-option__phone">{{slotProps.value.phone}}</span>
                </div>
                <div v-else class="flex" :style="{'min-height': '19px'}"></div>
              </template>
              <template #header>
                <div class="flex relative p-input-icon-right">
                  <InputText
                      v-model="filter"
                      class="flex mx-2 my-2 w-full"
                  />
                  <i class="pi pi-search" style="right: 20px"/>
                </div>
              </template>
              <template #empty>
                <span v-if="loading">Загрузка...</span>
                <span v-else>Пользователи не найдены</span>
              </template>
            </Dropdown>
            <label for="users" class="nowrap_label">Введите ФИО или номер телефона</label>
          </div>
          <span v-if="v$.user_id.$errors.length || errors?.user_id" class="text-xs color-error">
              {{ errors?.user_id ? errors.control_status[0] : v$.user_id.$errors[0].$message }}
            </span>
        </MainCard>
      </div>
    </div>
  </section>

  <section class="py-2 mb-3">
    <div class="grid">
      <div class="col-12">
        <MainCard title="Текст уведомления">
          <Editor v-model="form[textField]" class="w-full"/>
        </MainCard>
        <span class="text-xs color-error" v-if="v$[textField]?.$errors?.length">
            {{ v$[textField]?.$errors?.[0].$message }}
          </span>
      </div>
    </div>
  </section>
</template>